import { useState, useCallback } from 'react';
import {
  useCreatePageMutation,
  useCreatePageModuleMutation,
  useUpdatePageMutation,
} from '../../editCourse/editCourseApi';

export const useClassroomEditing = ({ courseId, sectionId, isDemo }) => {
  const [localPages, setLocalPages] = useState([]);
  const [localFiles, setLocalFiles] = useState([]);

  const [createPage] = useCreatePageMutation();
  const [createPageModule] = useCreatePageModuleMutation();
  const [updatePage] = useUpdatePageMutation();

  const addAssignment = useCallback(async ({ name, description, dueDate }) => {
    if (isDemo) {
      const newPage = {
        id: `demo-new-${Date.now()}`,
        name,
        type: 'task',
        description,
        dueDate,
        progress: null,
        isLocked: false,
      };
      setLocalPages((prev) => [...prev, newPage]);
      return newPage;
    }

    const pageResult = await createPage({ sectionId, courseId, name, order: 999 }).unwrap();
    const pageId = pageResult?.data?.id || pageResult?.id;
    if (pageId) {
      await createPageModule({ type: 'task', pageId, newRow: true }).unwrap();
    }
    return pageResult;
  }, [isDemo, courseId, sectionId, createPage, createPageModule]);

  const addFile = useCallback(async ({ name, size, type }) => {
    if (isDemo) {
      const newFile = {
        id: `demo-file-${Date.now()}`,
        name,
        size: size || 'Unknown',
        uploadedBy: 'Teacher',
        uploadedAt: 'Just now',
        type: type || 'pdf',
      };
      setLocalFiles((prev) => [...prev, newFile]);
      return newFile;
    }

    const pageResult = await createPage({ sectionId, courseId, name, order: 999 }).unwrap();
    const pageId = pageResult?.data?.id || pageResult?.id;
    if (pageId) {
      await createPageModule({ type: 'download', pageId, newRow: true }).unwrap();
    }
    return pageResult;
  }, [isDemo, courseId, sectionId, createPage, createPageModule]);

  const addDiscussion = useCallback(async ({ name }) => {
    if (isDemo) {
      const newPage = {
        id: `demo-disc-${Date.now()}`,
        name,
        type: 'discussion',
      };
      setLocalPages((prev) => [...prev, newPage]);
      return newPage;
    }

    const pageResult = await createPage({ sectionId, courseId, name, order: 999 }).unwrap();
    const pageId = pageResult?.data?.id || pageResult?.id;
    if (pageId) {
      await createPageModule({ type: 'discussion', pageId, newRow: true }).unwrap();
    }
    return pageResult;
  }, [isDemo, courseId, sectionId, createPage, createPageModule]);

  const editWebinarName = useCallback(async ({ pageId, name }) => {
    if (isDemo) {
      setLocalPages((prev) =>
        prev.map((p) => (p.id === pageId ? { ...p, name, topic: name } : p))
      );
      return;
    }

    await updatePage({ pageId, courseId, name }).unwrap();
  }, [isDemo, courseId, updatePage]);

  const addQuiz = useCallback(async ({ name, questionCount, timeLimit }) => {
    if (isDemo) {
      const newPage = {
        id: `demo-quiz-${Date.now()}`,
        name,
        type: 'quiz',
        questionCount: parseInt(questionCount, 10) || 10,
        timeLimit: `${timeLimit} min`,
        progress: null,
        isLocked: false,
      };
      setLocalPages((prev) => [...prev, newPage]);
      return newPage;
    }

    const pageResult = await createPage({ sectionId, courseId, name, order: 999 }).unwrap();
    const pageId = pageResult?.data?.id || pageResult?.id;
    if (pageId) {
      await createPageModule({ type: 'task', pageId, newRow: true }).unwrap();
    }
    return pageResult;
  }, [isDemo, courseId, sectionId, createPage, createPageModule]);

  return {
    localPages,
    localFiles,
    addAssignment,
    addFile,
    addDiscussion,
    editWebinarName,
    addQuiz,
  };
};
